/**
 * Right Toolbar — Node / Edge / Handle Properties Panel
 * Accordion sections with localStorage-persisted open state.
 * Node: editable name, icon, color, style. Edge: editable label, type, animated.
 * Handle: readonly port info + connection list.
 */

import React, { useState, useCallback, useRef } from 'react';
import { useWorkflowStore } from '@flow-studio/store';
import { useUIStore } from '@flow-studio/store';
import { getNodeDisplayProps } from '@flow-studio/types';
import { ReactIconPicker } from '@bizfirst/common-react';
import { StyleBuilderPanel } from '@atlas-forms/designer-components-react';
import type { StyleProperties } from '@atlas-forms/designer-components-react';
import { XIcon } from '../Icons';

// ─── Style summary helper ────────────────────────────────────────────────────

function buildStyleSummary(style: Record<string, unknown> | null | undefined): { label: string; bgColor?: string } {
  if (!style) return { label: 'No style applied' };
  const entries = Object.entries(style).filter(([, v]) => v !== undefined && v !== '' && v !== null);
  if (entries.length === 0) return { label: 'No style applied' };
  const bgColor = typeof style['backgroundColor'] === 'string' ? style['backgroundColor'] : undefined;
  const readable = entries.slice(0, 2).map(([k]) => k.replace(/([A-Z])/g, ' $1').toLowerCase());
  const extra = entries.length - 2;
  return { label: readable.join(', ') + (extra > 0 ? ` +${extra} more` : ''), bgColor };
}

// ─── localStorage-backed accordion state ────────────────────────────────────

const LS_KEY = 'flow-studio.properties.sections';

type SectionKey =
  // Node
  'generic' | 'design' | 'advanced' |
  // Edge
  'edge_generic' | 'edge_routing' | 'edge_advanced' |
  // Handle
  'handle_generic' | 'handle_behavior' | 'handle_connections';

const DEFAULTS: Record<SectionKey, boolean> = {
  generic: true, design: true, advanced: false,
  edge_generic: true, edge_routing: true, edge_advanced: false,
  handle_generic: true, handle_behavior: true, handle_connections: true,
};

function loadSections(): Record<SectionKey, boolean> {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { ...DEFAULTS };
}

function saveSections(state: Record<SectionKey, boolean>) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch { /* ignore */ }
}

function useAccordion() {
  const [open, setOpen] = useState<Record<SectionKey, boolean>>(loadSections);
  const toggle = useCallback((key: SectionKey) => {
    setOpen(prev => {
      const next = { ...prev, [key]: !prev[key] };
      saveSections(next);
      return next;
    });
  }, []);
  return { open, toggle };
}

// ─── Readonly Toggle ─────────────────────────────────────────────────────────

interface ReadonlyToggleProps { value: boolean; theme: 'dark' | 'light'; onLabel?: string; offLabel?: string; }

const ReadonlyToggle: React.FC<ReadonlyToggleProps> = ({ value, theme, onLabel = 'Yes', offLabel = 'No' }) => {
  const isDark = theme === 'dark';
  const trackOn  = isDark ? 'rgba(150,160,155,0.35)' : 'rgba(107,120,115,0.35)';
  const trackOff = isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb';
  const textOn   = isDark ? 'rgba(200,210,205,0.75)' : '#6b7280';
  const textOff  = isDark ? 'rgba(255,255,255,0.2)'  : '#d1d5db';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', cursor: 'default' }}>
      <div style={{ width: '32px', height: '18px', borderRadius: '9px', background: value ? trackOn : trackOff, position: 'relative', flexShrink: 0 }}>
        <div style={{ position: 'absolute', top: '2px', left: value ? '16px' : '2px', width: '14px', height: '14px', borderRadius: '50%', background: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.9)', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
      </div>
      <span style={{ fontSize: '12px', color: value ? textOn : textOff }}>{value ? onLabel : offLabel}</span>
    </div>
  );
};

// ─── Interactive Toggle ───────────────────────────────────────────────────────

interface InteractiveToggleProps { value: boolean; onChange: (v: boolean) => void; theme: 'dark' | 'light'; onLabel?: string; offLabel?: string; }

const InteractiveToggle: React.FC<InteractiveToggleProps> = ({ value, onChange, theme, onLabel = 'Yes', offLabel = 'No' }) => {
  const isDark = theme === 'dark';
  const trackOn  = '#309B48';
  const trackOff = isDark ? 'rgba(255,255,255,0.12)' : '#e5e7eb';
  const textOn   = isDark ? '#86efac' : '#16a34a';
  const textOff  = isDark ? 'rgba(255,255,255,0.4)' : '#9ca3af';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', cursor: 'pointer' }} onClick={() => onChange(!value)}>
      <div style={{ width: '32px', height: '18px', borderRadius: '9px', background: value ? trackOn : trackOff, position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
        <div style={{ position: 'absolute', top: '2px', left: value ? '16px' : '2px', width: '14px', height: '14px', borderRadius: '50%', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transition: 'left 0.2s' }} />
      </div>
      <span style={{ fontSize: '12px', color: value ? textOn : textOff }}>{value ? onLabel : offLabel}</span>
    </div>
  );
};

// ─── Accordion Section ───────────────────────────────────────────────────────

interface AccordionProps {
  title: string;
  sectionKey: SectionKey;
  isOpen: boolean;
  onToggle: () => void;
  theme: 'dark' | 'light';
  children: React.ReactNode;
}

const AccordionSection: React.FC<AccordionProps> = ({ title, isOpen, onToggle, theme, children }) => {
  const isDark = theme === 'dark';
  return (
    <div style={{ marginBottom: '4px', borderRadius: '8px', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e5e7eb'}` }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '9px 14px', background: isDark ? 'rgba(255,255,255,0.04)' : '#f9fafb',
          border: 'none', borderRadius: isOpen ? '8px 8px 0 0' : '8px', cursor: 'pointer',
          color: isDark ? 'rgba(48,155,72,0.95)' : '#309B48',
          fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em',
        }}
      >
        <span>{title}</span>
        <span style={{ fontSize: '14px', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', color: isDark ? 'rgba(255,255,255,0.4)' : '#9ca3af' }}>▾</span>
      </button>
      {isOpen && (
        <div style={{ padding: '14px', background: isDark ? 'rgba(0,0,0,0.18)' : '#ffffff', borderRadius: '0 0 8px 8px' }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

export const RightToolbar: React.FC = () => {
  const { nodes, edges, updateNode, updateProcessElement, updateEdge } = useWorkflowStore();
  const { rightPanelOpen, toggleRightPanel, selectedNodes, selectedEdges, selectedHandle, theme } = useUIStore();
  const { open, toggle } = useAccordion();
  const colorInputRef           = useRef<HTMLInputElement>(null);
  const stylePanelBtnRef        = useRef<HTMLButtonElement>(null);
  const headerStylePanelBtnRef  = useRef<HTMLButtonElement>(null);
  const edgeStylePanelBtnRef    = useRef<HTMLButtonElement>(null);
  const labelStylePanelBtnRef   = useRef<HTMLButtonElement>(null);
  const [showStylePanel,        setShowStylePanel]        = useState(false);
  const [showHeaderStylePanel,  setShowHeaderStylePanel]  = useState(false);
  const [showEdgeStylePanel,    setShowEdgeStylePanel]    = useState(false);
  const [showLabelStylePanel,   setShowLabelStylePanel]   = useState(false);

  const selectedNode = selectedNodes.length === 1 ? nodes.find(n => n.id === selectedNodes[0]) : null;
  const selectedEdge = selectedEdges.length === 1 ? edges.find(e => e.id === selectedEdges[0]) : null;

  // Deep-patch configuration.designer.ui
  const updateUi = useCallback((nodeId: string, patch: Record<string, unknown>) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;
    const pe = node.data.processElement;
    updateNode(nodeId, {
      data: {
        ...node.data,
        processElement: {
          ...pe,
          configuration: {
            ...(pe?.configuration),
            designer: {
              ...(pe?.configuration?.designer),
              ui: { ...(pe?.configuration?.designer?.ui), ...patch },
            },
          },
        },
      },
    } as any);
  }, [nodes, updateNode]);

  if (!rightPanelOpen) return null;

  const isDark = theme === 'dark';

  // ── Shared styles ──────────────────────────────────────────────────────────
  const toolbarStyle: React.CSSProperties = {
    width: '300px',
    background: isDark ? 'rgba(10, 13, 20, 0.92)' : '#ffffff',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderLeft: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e5e7eb'}`,
    display: 'flex', flexDirection: 'column', overflowY: 'auto', overflowX: 'visible',
    zIndex: 100,
  };

  const panelHeaderStyle: React.CSSProperties = {
    padding: '6.5px 12px 6.5px 24px',
    borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e5e7eb'}`,
    fontSize: '12px', fontWeight: 600,
    background: isDark ? 'linear-gradient(135deg, #1a4d2a 0%, #0d2516 100%)' : 'linear-gradient(135deg, #309B48 0%, #1E5F2E 100%)',
    flexShrink: 0, position: 'sticky', top: 0, zIndex: 10,
    color: '#ffffff',
  };

  const scrollStyle: React.CSSProperties = { padding: '10px', display: 'flex', flexDirection: 'column', gap: '0' };

  const fieldLabelStyle = (): React.CSSProperties => ({
    fontSize: '11px', fontWeight: 500, marginBottom: '4px',
    color: isDark ? 'rgba(150,160,155,0.85)' : '#6B7879',
  });

  const field = (label: string, value: React.ReactNode) => (
    <div style={{ marginBottom: '12px' }}>
      <div style={fieldLabelStyle()}>{label}</div>
      {value}
    </div>
  );

  const inputVal = (val: string) => (
    <div style={{
      padding: '6px 10px', borderRadius: '5px', fontSize: '12px',
      background: isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e5e7eb'}`,
      color: isDark ? 'rgba(220,225,222,0.9)' : '#111827', wordBreak: 'break-all',
    }}>
      {val || '—'}
    </div>
  );

  const editableInput = (val: string, onChange: (v: string) => void, placeholder?: string) => (
    <input
      value={val}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder || '—'}
      style={{
        width: '100%', padding: '6px 10px', borderRadius: '5px', fontSize: '12px',
        background: isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#d1d5db'}`,
        color: isDark ? 'rgba(220,225,222,0.9)' : '#111827',
        outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s',
      }}
      onFocus={e => { e.target.style.borderColor = '#309B48'; }}
      onBlur={e => { e.target.style.borderColor = isDark ? 'rgba(255,255,255,0.12)' : '#d1d5db'; }}
    />
  );

  const selectField = (val: string, options: { value: string; label: string }[], onChange: (v: string) => void) => (
    <select
      value={val}
      onChange={e => onChange(e.target.value)}
      style={{
        width: '100%', padding: '6px 10px', borderRadius: '5px', fontSize: '12px',
        background: isDark ? '#0f172a' : '#f9fafb',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#d1d5db'}`,
        color: isDark ? 'rgba(220,225,222,0.9)' : '#111827',
        outline: 'none', cursor: 'pointer',
        colorScheme: isDark ? 'dark' : 'light' as any,
      }}
    >
      {options.map(o => (
        <option
          key={o.value} value={o.value}
          style={{ background: isDark ? '#0f172a' : '#ffffff', color: isDark ? 'rgba(220,225,222,0.9)' : '#111827' }}
        >
          {o.label}
        </option>
      ))}
    </select>
  );

  const badge = (text: string, color?: string) => (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 600,
      background: color ? `${color}22` : isDark ? 'rgba(255,255,255,0.08)' : '#f3f4f6',
      color: color || (isDark ? 'rgba(220,225,222,0.75)' : '#374151'),
      border: `1px solid ${color ? `${color}44` : isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
    }}>
      {text}
    </span>
  );

  const emptyStateStyle: React.CSSProperties = {
    padding: '32px 16px', textAlign: 'center',
    color: isDark ? '#6B7879' : '#9ca3af', fontSize: '13px',
  };

  // ── Determine which handle is selected ────────────────────────────────────
  const handlePanel = selectedHandle
    ? (() => {
        const { nodeId, handleId, handleType } = selectedHandle;
        const node = nodes.find(n => n.id === nodeId);
        if (!node) return null;
        const display = getNodeDisplayProps(node.data);
        const port = handleType === 'target'
          ? display.inputPorts.find(p => p.portKey === handleId)
          : display.outputPorts.find(p => p.portKey === handleId);
        if (!port) return null;

        const connections = edges.filter(e =>
          handleType === 'source'
            ? e.source === nodeId && e.sourceHandle === handleId
            : e.target === nodeId && e.targetHandle === handleId
        );

        const connectedNodes = connections.map(conn => {
          const connId = handleType === 'source' ? conn.target : conn.source;
          const connNode = nodes.find(n => n.id === connId);
          return {
            name: connNode ? getNodeDisplayProps(connNode.data).displayName : connId,
            handleId: handleType === 'source' ? conn.targetHandle : conn.sourceHandle,
          };
        });

        return { node, display, port, connections, connectedNodes };
      })()
    : null;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="right-toolbar" style={toolbarStyle}>
      <style>{`
        .right-toolbar::-webkit-scrollbar { width: 6px; }
        .right-toolbar::-webkit-scrollbar-track { background: transparent; }
        .right-toolbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.14); border-radius: 3px; }
        .right-toolbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.24); }
        .rt-icon-picker .rip__input { padding: 5px 8px !important; font-size: 12px !important; }
        .rt-icon-picker .rip__label { font-size: 12px !important; }
        .rt-icon-picker .rip__selected { width: 20px !important; height: 20px !important; }
        /* Explicit close affordance for mobile/tablet (<1024px), where this panel renders as a
           fixed full-screen-ish slide-in overlay (see styles/index.css's ".right-toolbar" mobile
           rule) that visually covers the canvas' own collapse-tab toggle button, leaving no
           reachable way to dismiss it besides an undocumented Escape — real bug: Escape only
           clears the node/edge/handle selection (falling through to the near-empty "Properties"
           state below) rather than actually closing the panel, so mobile users were stuck staring
           at a mostly-blank overlay with the node palette/canvas blocked behind it. */
        .rt-mobile-close { display: none; }
        @media (max-width: 1023px) {
          .rt-mobile-close { display: flex; }
        }
      `}</style>
      <button
        type="button"
        className="rt-mobile-close"
        onClick={toggleRightPanel}
        aria-label="Close properties panel"
        title="Close"
        style={{
          position: 'absolute', top: '8px', right: '8px', zIndex: 20,
          width: '32px', height: '32px', alignItems: 'center', justifyContent: 'center',
          borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: 'rgba(0,0,0,0.35)',
        }}
      >
        <XIcon size={16} color="#ffffff" />
      </button>

      {/* ══ HANDLE PROPERTIES ══════════════════════════════════════════════ */}
      {handlePanel && (() => {
        const { node, port, connections, connectedNodes } = handlePanel;
        const isOutput = port.portType === 'output';
        const accentColor = isOutput ? '#309B48' : '#3b82f6';

        const updatePort = (patch: Record<string, unknown>) => {
          const pe = node.data.processElement;
          const updatedPorts = (pe?.ports ?? []).map((p: any) =>
            p.portKey === port.portKey ? { ...p, ...patch } : p
          );
          updateProcessElement(node.id, { ports: updatedPorts });
        };

        const handleStyleOptions = [
          { value: 'arrow',   label: 'Arrow (default)' },
          { value: 'circle',  label: 'Circle' },
          { value: 'square',  label: 'Square' },
          { value: 'diamond', label: 'Diamond' },
        ];

        const positionOptions = [
          { value: 'top',    label: 'Top' },
          { value: 'right',  label: 'Right' },
          { value: 'bottom', label: 'Bottom' },
          { value: 'left',   label: 'Left' },
        ];

        return (
          <>
            <div className="studio-properties-heading" style={{ ...panelHeaderStyle, color: '#fff', background: isDark ? `linear-gradient(135deg, ${accentColor}33 0%, ${accentColor}11 100%)` : `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}cc 100%)` }}>
              {isOutput ? 'Output Port' : 'Input Port'} Properties
            </div>
            <div style={scrollStyle}>
              <AccordionSection title="Generic" sectionKey="handle_generic" isOpen={open.handle_generic} onToggle={() => toggle('handle_generic')} theme={theme}>
                {field('Handle Name', inputVal(port.label || port.portKey))}
                {field('Handle ID',   inputVal(port.portKey))}
                {field('Type',        <div style={{ padding: '4px 0' }}>{badge(isOutput ? 'Output' : 'Input', accentColor)}</div>)}
                {field('Position', selectField(
                  port.position || 'right',
                  positionOptions,
                  val => updatePort({ position: val })
                ))}
                {field('Handle Style', selectField(
                  port.handleStyle || (port.portType === 'input' ? 'arrow' : 'circle'),
                  handleStyleOptions,
                  val => updatePort({ handleStyle: val })
                ))}
                {port.color && field('Color', (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
                    <span style={{ width: '14px', height: '14px', borderRadius: '3px', background: port.color, border: '1px solid rgba(255,255,255,0.2)', flexShrink: 0 }} />
                    <span style={{ fontSize: '12px', color: isDark ? 'rgba(220,225,222,0.7)' : '#374151' }}>{port.color}</span>
                  </div>
                ))}
              </AccordionSection>

              <AccordionSection title="Behavior" sectionKey="handle_behavior" isOpen={open.handle_behavior} onToggle={() => toggle('handle_behavior')} theme={theme}>
                {field('Main Port', (
                  <InteractiveToggle
                    value={!!port.isMainPort}
                    onChange={val => updatePort({ isMainPort: val })}
                    theme={theme} onLabel="Yes" offLabel="No"
                  />
                ))}
                {field('Required', (
                  <InteractiveToggle
                    value={!!port.isRequired}
                    onChange={val => updatePort({ isRequired: val })}
                    theme={theme} onLabel="Yes" offLabel="No"
                  />
                ))}
                {port.isMultiple !== undefined && field('Multi-connect', <ReadonlyToggle value={!!port.isMultiple} theme={theme} />)}
                {port.isErrorPort !== undefined && field('Error Port',   <ReadonlyToggle value={!!port.isErrorPort} theme={theme} />)}
              </AccordionSection>

              <AccordionSection title={`Connections (${connections.length})`} sectionKey="handle_connections" isOpen={open.handle_connections} onToggle={() => toggle('handle_connections')} theme={theme}>
                {connections.length === 0
                  ? <div style={{ fontSize: '12px', color: isDark ? 'rgba(255,255,255,0.3)' : '#9ca3af', padding: '4px 0' }}>No connections</div>
                  : connectedNodes.map((c, i) => (
                    <div key={i} style={{ marginBottom: '8px', padding: '7px 9px', borderRadius: '5px', background: isDark ? 'rgba(255,255,255,0.04)' : '#f9fafb', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e5e7eb'}` }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: isDark ? 'rgba(220,225,222,0.9)' : '#111827' }}>{c.name}</div>
                      <div style={{ fontSize: '11px', color: isDark ? 'rgba(255,255,255,0.35)' : '#9ca3af', marginTop: '2px' }}>
                        {isOutput ? 'To' : 'From'} handle: {c.handleId || 'main'}
                      </div>
                    </div>
                  ))
                }
              </AccordionSection>
            </div>
          </>
        );
      })()}

      {/* ══ NODE PROPERTIES ════════════════════════════════════════════════ */}
      {!handlePanel && selectedNode && (() => {
        const display = getNodeDisplayProps(selectedNode.data);
        const pe = selectedNode.data.processElement;
        const nodeId = selectedNode.id;
        const currentColor = display.color || '#309B48';
        const currentIcon  = display.icon || '';

        return (
          <>
            <div className="studio-properties-heading" style={{ ...panelHeaderStyle, color: '#fff' }}>Node Properties</div>
            <div style={scrollStyle}>

              <AccordionSection title="Generic" sectionKey="generic" isOpen={open.generic} onToggle={() => toggle('generic')} theme={theme}>
                {field('Name', editableInput(display.displayName, val => updateProcessElement(nodeId, { displayName: val }), 'Node name...'))}
                {field('Type',     inputVal(selectedNode.type || '—'))}
                {field('Category', inputVal(display.category || '—'))}
                {field('Status', (
                  <InteractiveToggle
                    value={!pe?.isDisabled}
                    onChange={val => updateProcessElement(nodeId, { isDisabled: !val })}
                    theme={theme} onLabel="Enabled" offLabel="Disabled"
                  />
                ))}
                {field('Description', (
                  <textarea
                    value={pe?.description ?? ''}
                    onChange={e => updateProcessElement(nodeId, { description: e.target.value })}
                    placeholder="Node description..."
                    rows={3}
                    style={{
                      width: '100%', padding: '6px 10px', borderRadius: '5px', fontSize: '12px',
                      background: isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#d1d5db'}`,
                      color: isDark ? 'rgba(220,225,222,0.9)' : '#111827',
                      outline: 'none', boxSizing: 'border-box', resize: 'vertical',
                      lineHeight: 1.5, fontFamily: 'inherit', transition: 'border-color 0.15s',
                    }}
                    onFocus={e => { e.target.style.borderColor = '#309B48'; }}
                    onBlur={e => { e.target.style.borderColor = isDark ? 'rgba(255,255,255,0.12)' : '#d1d5db'; }}
                  />
                ))}
              </AccordionSection>

              <AccordionSection title="Design" sectionKey="design" isOpen={open.design} onToggle={() => toggle('design')} theme={theme}>
                {field('Icon', (
                  <div className="rt-icon-picker">
                    <ReactIconPicker value={currentIcon} onChange={iconName => updateUi(nodeId, { icon: iconName })} size={14} placeholder="Select icon..." />
                  </div>
                ))}

                {field('Color', (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', borderRadius: '5px', fontSize: '12px', background: isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e5e7eb'}`, color: isDark ? 'rgba(220,225,222,0.9)' : '#111827', cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ width: '16px', height: '16px', borderRadius: '3px', background: currentColor, flexShrink: 0, border: '1px solid rgba(255,255,255,0.15)', boxShadow: '0 0 0 1px rgba(0,0,0,0.3)' }} />
                    <span style={{ flex: 1 }}>{currentColor}</span>
                    <span style={{ fontSize: '10px', color: isDark ? 'rgba(255,255,255,0.3)' : '#9ca3af' }}>✏</span>
                    <input ref={colorInputRef} type="color" value={currentColor} onChange={e => updateUi(nodeId, { color: e.target.value })} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', border: 'none', padding: 0 }} />
                  </label>
                ))}

                {field('Node Style', (
                  <>
                    {(() => {
                      const rawStyle = selectedNode.data.processElement?.configuration?.designer?.ui?.style as Record<string, unknown> | undefined;
                      const { label: styleLabel, bgColor: styleBgColor } = buildStyleSummary(rawStyle);
                      const hasStyle = styleLabel !== 'No style applied';
                      return (
                        <button
                          ref={stylePanelBtnRef}
                          onClick={() => setShowStylePanel(true)}
                          style={{ width: '100%', padding: '6px 10px', borderRadius: '5px', fontSize: '12px', background: isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#d1d5db'}`, color: isDark ? (hasStyle ? 'rgba(220,225,222,0.9)' : 'rgba(255,255,255,0.3)') : (hasStyle ? '#111827' : '#9ca3af'), cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                          {styleBgColor && <span style={{ width: '12px', height: '12px', borderRadius: '2px', background: styleBgColor, flexShrink: 0, border: '1px solid rgba(255,255,255,0.2)' }} />}
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{styleLabel}</span>
                          <span style={{ fontSize: '10px', color: isDark ? 'rgba(255,255,255,0.35)' : '#9ca3af', flexShrink: 0 }}>✏</span>
                        </button>
                      );
                    })()}
                    {showStylePanel && stylePanelBtnRef.current && (
                      <StyleBuilderPanel
                        slot="nodeStyle" slotLabel="Node Style"
                        value={(selectedNode.data.processElement?.configuration?.designer?.ui?.style as StyleProperties) ?? {}}
                        anchorEl={stylePanelBtnRef.current} isDark={isDark}
                        onApply={styles => { updateUi(nodeId, { style: styles }); setShowStylePanel(false); }}
                        onClose={() => setShowStylePanel(false)}
                      />
                    )}
                  </>
                ))}

                {field('Header Style', (
                  <>
                    {(() => {
                      const rawHeaderStyle = selectedNode.data.processElement?.configuration?.designer?.ui?.headerStyle as Record<string, unknown> | undefined;
                      const { label: headerStyleLabel, bgColor: headerStyleBgColor } = buildStyleSummary(rawHeaderStyle);
                      const hasHeaderStyle = headerStyleLabel !== 'No style applied';
                      return (
                        <button
                          ref={headerStylePanelBtnRef}
                          onClick={() => setShowHeaderStylePanel(true)}
                          style={{ width: '100%', padding: '6px 10px', borderRadius: '5px', fontSize: '12px', background: isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#d1d5db'}`, color: isDark ? (hasHeaderStyle ? 'rgba(220,225,222,0.9)' : 'rgba(255,255,255,0.3)') : (hasHeaderStyle ? '#111827' : '#9ca3af'), cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                          {headerStyleBgColor && <span style={{ width: '12px', height: '12px', borderRadius: '2px', background: headerStyleBgColor, flexShrink: 0, border: '1px solid rgba(255,255,255,0.2)' }} />}
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{headerStyleLabel}</span>
                          <span style={{ fontSize: '10px', color: isDark ? 'rgba(255,255,255,0.35)' : '#9ca3af', flexShrink: 0 }}>✏</span>
                        </button>
                      );
                    })()}
                    {showHeaderStylePanel && headerStylePanelBtnRef.current && (
                      <StyleBuilderPanel
                        slot="headerStyle" slotLabel="Header Style"
                        value={(selectedNode.data.processElement?.configuration?.designer?.ui?.headerStyle as StyleProperties) ?? {}}
                        anchorEl={headerStylePanelBtnRef.current} isDark={isDark}
                        onApply={styles => { updateUi(nodeId, { headerStyle: styles }); setShowHeaderStylePanel(false); }}
                        onClose={() => setShowHeaderStylePanel(false)}
                      />
                    )}
                  </>
                ))}
                {field('Header Image URL', editableInput(
                  selectedNode.data.processElement?.configuration?.designer?.ui?.headerImageUrl ?? '',
                  v => updateUi(nodeId, { headerImageUrl: v }),
                  'https://…'
                ))}

                {field('Body Image URL', editableInput(
                  selectedNode.data.processElement?.configuration?.designer?.ui?.bodyImageUrl ?? '',
                  v => updateUi(nodeId, { bodyImageUrl: v }),
                  'https://…'
                ))}
              </AccordionSection>

              <AccordionSection title="Advanced" sectionKey="advanced" isOpen={open.advanced} onToggle={() => toggle('advanced')} theme={theme}>
                {field('Node ID',    inputVal(selectedNode.id))}
                {field('Element ID', inputVal(String(display.processElementID ?? '—')))}
                {field('Is Trigger',     <ReadonlyToggle value={pe?.isTrigger ?? false}       theme={theme} />)}
                {field('Continue on Fail', <ReadonlyToggle value={pe?.continueOnFail ?? false} theme={theme} />)}
                {field('Always Output',    <ReadonlyToggle value={pe?.alwaysOutputData ?? false} theme={theme} />)}
                {pe?.notes && field('Notes', (
                  <div style={{ padding: '6px 10px', borderRadius: '5px', fontSize: '12px', background: isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#e5e7eb'}`, color: isDark ? 'rgba(220,225,222,0.9)' : '#111827', lineHeight: 1.5 }}>
                    {pe?.notes}
                  </div>
                ))}
              </AccordionSection>

            </div>
          </>
        );
      })()}

      {/* ══ CONNECTION PROPERTIES ══════════════════════════════════════════ */}
      {!handlePanel && selectedEdge && !selectedNode && (() => {
        const sourceNode = nodes.find(n => n.id === selectedEdge.source);
        const targetNode = nodes.find(n => n.id === selectedEdge.target);
        const sourceName = sourceNode ? getNodeDisplayProps(sourceNode.data).displayName : selectedEdge.source;
        const targetName = targetNode ? getNodeDisplayProps(targetNode.data).displayName : selectedEdge.target;

        const edgeTypeOptions = [
          { value: 'smoothstep', label: 'Smooth Step (default)' },
          { value: 'default',    label: 'Bezier' },
          { value: 'step',       label: 'Step' },
          { value: 'straight',   label: 'Straight' },
        ];

        return (
          <>
            <div className="studio-properties-heading" style={{ ...panelHeaderStyle, color: '#fff' }}>Connection Properties</div>
            <div style={scrollStyle}>

              {/* Editable fields */}
              <AccordionSection title="Generic" sectionKey="edge_generic" isOpen={open.edge_generic} onToggle={() => toggle('edge_generic')} theme={theme}>
                {field('Label', editableInput(
                  typeof selectedEdge.label === 'string' ? selectedEdge.label : '',
                  val => updateEdge(selectedEdge.id, { label: val || undefined }),
                  'Connection label...'
                ))}
                {field('Connection Type', selectField(
                  selectedEdge.type || 'smoothstep',
                  edgeTypeOptions,
                  val => updateEdge(selectedEdge.id, { type: val as any, data: { ...selectedEdge.data, edgeType: val } })
                ))}
                {field('Marker Style', selectField(
                  (selectedEdge.data as any)?.markerStyle || 'arrow',
                  [
                    { value: 'arrow',   label: 'Arrow (default)' },
                    { value: 'circle',  label: 'Circle' },
                    { value: 'square',  label: 'Square' },
                    { value: 'diamond', label: 'Diamond' },
                  ],
                  val => updateEdge(selectedEdge.id, { data: { ...selectedEdge.data, markerStyle: val } })
                ))}
                {field('Animated', (
                  <InteractiveToggle
                    value={!!selectedEdge.animated}
                    onChange={val => updateEdge(selectedEdge.id, { animated: val })}
                    theme={theme}
                    onLabel="Animated"
                    offLabel="Static"
                  />
                ))}
                {field('Connection Style', (
                  <>
                    {(() => {
                      const rawStyle = selectedEdge.style as Record<string, unknown> | undefined;
                      const { label: styleLabel, bgColor: styleBgColor } = buildStyleSummary(rawStyle);
                      const hasStyle = styleLabel !== 'No style applied';
                      return (
                        <button
                          ref={edgeStylePanelBtnRef}
                          onClick={() => setShowEdgeStylePanel(true)}
                          style={{ width: '100%', padding: '6px 10px', borderRadius: '5px', fontSize: '12px', background: isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#d1d5db'}`, color: isDark ? (hasStyle ? 'rgba(220,225,222,0.9)' : 'rgba(255,255,255,0.3)') : (hasStyle ? '#111827' : '#9ca3af'), cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                          {styleBgColor && <span style={{ width: '12px', height: '12px', borderRadius: '2px', background: styleBgColor, flexShrink: 0, border: '1px solid rgba(255,255,255,0.2)' }} />}
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{styleLabel}</span>
                          <span style={{ fontSize: '10px', color: isDark ? 'rgba(255,255,255,0.35)' : '#9ca3af', flexShrink: 0 }}>✏</span>
                        </button>
                      );
                    })()}
                    {showEdgeStylePanel && edgeStylePanelBtnRef.current && (
                      <StyleBuilderPanel
                        slot="edgeStyle" slotLabel="Connection Style"
                        value={(selectedEdge.style as StyleProperties) ?? {}}
                        anchorEl={edgeStylePanelBtnRef.current} isDark={isDark}
                        onApply={styles => { updateEdge(selectedEdge.id, { style: styles as any }); setShowEdgeStylePanel(false); }}
                        onClose={() => setShowEdgeStylePanel(false)}
                      />
                    )}
                  </>
                ))}
                {field('Label Style', (
                  <>
                    {(() => {
                      const rawStyle = selectedEdge.labelStyle as Record<string, unknown> | undefined;
                      const { label: styleLabel, bgColor: styleBgColor } = buildStyleSummary(rawStyle);
                      const hasStyle = styleLabel !== 'No style applied';
                      return (
                        <button
                          ref={labelStylePanelBtnRef}
                          onClick={() => setShowLabelStylePanel(true)}
                          style={{ width: '100%', padding: '6px 10px', borderRadius: '5px', fontSize: '12px', background: isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#d1d5db'}`, color: isDark ? (hasStyle ? 'rgba(220,225,222,0.9)' : 'rgba(255,255,255,0.3)') : (hasStyle ? '#111827' : '#9ca3af'), cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                          {styleBgColor && <span style={{ width: '12px', height: '12px', borderRadius: '2px', background: styleBgColor, flexShrink: 0, border: '1px solid rgba(255,255,255,0.2)' }} />}
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{styleLabel}</span>
                          <span style={{ fontSize: '10px', color: isDark ? 'rgba(255,255,255,0.35)' : '#9ca3af', flexShrink: 0 }}>✏</span>
                        </button>
                      );
                    })()}
                    {showLabelStylePanel && labelStylePanelBtnRef.current && (
                      <StyleBuilderPanel
                        slot="labelStyle" slotLabel="Label Style"
                        value={(selectedEdge.labelStyle as StyleProperties) ?? {}}
                        anchorEl={labelStylePanelBtnRef.current} isDark={isDark}
                        onApply={styles => { updateEdge(selectedEdge.id, { labelStyle: styles as any }); setShowLabelStylePanel(false); }}
                        onClose={() => setShowLabelStylePanel(false)}
                      />
                    )}
                  </>
                ))}
                {field('Label Show BG', (
                  <InteractiveToggle
                    value={selectedEdge.labelShowBg !== false}
                    onChange={val => updateEdge(selectedEdge.id, { labelShowBg: val })}
                    theme={theme}
                    onLabel="Visible"
                    offLabel="Hidden"
                  />
                ))}
              </AccordionSection>

              {/* Routing info */}
              <AccordionSection title="Routing" sectionKey="edge_routing" isOpen={open.edge_routing} onToggle={() => toggle('edge_routing')} theme={theme}>
                {field('Source Node',   inputVal(sourceName))}
                {field('Source Handle', inputVal(selectedEdge.sourceHandle || 'main'))}
                {field('Target Node',   inputVal(targetName))}
                {field('Target Handle', inputVal(selectedEdge.targetHandle || 'main'))}
                {field('Connection ID', inputVal(selectedEdge.id))}
                {field('Z-Index', editableInput(
                  String(selectedEdge.zIndex ?? 0),
                  val => { const n = parseInt(val); if (!isNaN(n)) updateEdge(selectedEdge.id, { zIndex: n }); },
                  '0'
                ))}
              </AccordionSection>

              {/* Advanced editable flags */}
              <AccordionSection title="Advanced" sectionKey="edge_advanced" isOpen={open.edge_advanced} onToggle={() => toggle('edge_advanced')} theme={theme}>
                {field('Hidden', (
                  <InteractiveToggle
                    value={!!selectedEdge.hidden}
                    onChange={val => updateEdge(selectedEdge.id, { hidden: val })}
                    theme={theme} onLabel="Hidden" offLabel="Visible"
                  />
                ))}
                {field('Focusable', (
                  <InteractiveToggle
                    value={(selectedEdge as any).focusable ?? true}
                    onChange={val => updateEdge(selectedEdge.id, { focusable: val } as any)}
                    theme={theme} onLabel="Yes" offLabel="No"
                  />
                ))}
                {field('Deletable', (
                  <InteractiveToggle
                    value={(selectedEdge as any).deletable ?? true}
                    onChange={val => updateEdge(selectedEdge.id, { deletable: val } as any)}
                    theme={theme} onLabel="Yes" offLabel="No"
                  />
                ))}
                {field('Selectable', (
                  <InteractiveToggle
                    value={(selectedEdge as any).selectable ?? true}
                    onChange={val => updateEdge(selectedEdge.id, { selectable: val } as any)}
                    theme={theme} onLabel="Yes" offLabel="No"
                  />
                ))}
                {field('Interaction Width', editableInput(
                  String((selectedEdge as any).interactionWidth ?? 20),
                  val => { const n = parseInt(val); if (!isNaN(n)) updateEdge(selectedEdge.id, { interactionWidth: n } as any); },
                  '20'
                ))}
                {field('Class Name', editableInput(
                  selectedEdge.className || '',
                  val => updateEdge(selectedEdge.id, { className: val || undefined }),
                  'CSS class name...'
                ))}
              </AccordionSection>

            </div>
          </>
        );
      })()}

      {/* ══ EMPTY STATE ═════════════════════════════════════════════════════ */}
      {!handlePanel && !selectedNode && !selectedEdge && (
        <>
          <div className="studio-properties-heading" style={{ ...panelHeaderStyle, color: '#fff' }}>Properties</div>
          <div className="studio-properties-empty" style={emptyStateStyle}>Select a node, connection, or port to view properties</div>
        </>
      )}
    </div>
  );
};
